import React, { useEffect, useRef, useState } from "react";
import { FlexRow, H1, Button, FlexColumn } from "appStyles";
import styled, { css } from "react-emotion";
import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from "@material-ui/core/styles";
import { withRedux, priceToDollars, formatDollarsToCents } from "helpers";
import { TextArea } from "components";

import NumberFormat from "react-number-format";
import Modal from "@material-ui/core/Modal";
import AddressModal from "./AddressModal";
import CloseIcon from "@material-ui/icons/Close";
import Switch from "@material-ui/core/Switch";
import AlertModal from "../../components/Tools/AlertModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FlexRight } from "../../app.styles";
import { Tooltip } from "@material-ui/core";
import HelpTwoToneIcon from "@material-ui/icons/HelpTwoTone";
import Alert from "@material-ui/lab/Alert";

const storeContainer = css`
  margin: 0px 20px;
  @media only screen and (min-width: 768px) {
    margin: 0px 40px;
  }
`;

const P = styled("p")`
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 13px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  display: block;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
`;

const arrowStyle = css`
  font-size: 1.3em;
  display: flex;
  align-self: flex-end;
  margin-top: 10px;
  margin-bottom: 10px;
  @media only screen and (min-width: 768px) {
    font-size: 2em;
    cursor: pointer;
    display: flex;
    align-self: flex-end;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`;

const boldButton = css`
  display: inline-block;
  width: auto;
  text-align: center;
  border-radius: 5.5px;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-transform: none;
  box-shadow: rgba(120, 120, 120, 0.5);
`;

const headerText = css`
  text-align: center;
  font-size: 1.5em;
  margin: 0px;
  @media only screen and (min-width: 768px) {
    text-align: center;
    font-size: 2.5em;
    margin: 0px;
  }
`;

const mdcButton = css`
  background-color: #89d2d3;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-transform: none;
  width: auto;
  box-shadow: rgba(120, 120, 120, 0.5) 3px 3px 13px;
`;
const titleStyle = css`
  white-space: nowrap;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;
  font-size: 15px;
  line-height: 18px;
`;

const headerContainer = css`
  height: 25px;
  align-items: center;
  margin: 10px 0px;
  justify-content: center;
  @media only screen and (min-width: 768px) {
    height: 25px;
    align-items: center;
    margin: 20px 0px;
    justify-content: center;
  }
`;

const inputStyle = css`
  width: 100%;
  border: none;
  border: 1px solid #979797;
  background: none;
  outline: none;
  margin: 0 0 10;
  box-shadow: none;
  margin-bottom: 10px;
  padding: 6px 10px;
  color: #1f3747;
  font-size: 15px;
  box-sizing: border-box;
  border-radius: 3px;
  font-family: "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva,
    Verdana, sans-serif;

  &:hover,
  :focus {
    border-color: #aed9da;
    opacity: 1;
    transition: opacity 0.4s;
  }

  &::placeholder {
    color: grey;
    font-size: 1rem;
    opacity: 0.8;
    margin: 5px;
  }
`;

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#F17F7F",
    },
    secondary: {
      main: "#88d1d4",
    },
  },
  overrides: {
    // Style sheet name ⚛️
    MuiTablePagination: {
      // Name of the rule
      actions: {
        // Some CSS
        marginLeft: "0px",
      },
    },
    MuiTableContainer: {
      root: {
        flex: 1,
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        width: "100%",
      },
    },
    MuiTableSortLabel: {
      root: {
        color: "#89D2D3 !important",
      },
    },
  },
});

const getShippingTipText = (method) => {
  switch (method) {
    case "PICKUP":
      return `We’ll show this message to your customer before they select the pickup option. You can put information here to tell your customer how pick up works for your business.`;
    case "SHIPPING":
      return `We’ll show this message to your customer before they select the shipping option. You can put information here to tell your customer how shipping works for your business.`;
    case "DELIVERY":
      return `We’ll show this message to your customer before they select the delivery option. You can put information here to tell your customer how delivery works for your business.`;
    case "SHIPPO":
      return `Shipping is tricky and can vary significantly. A lot of businesses will either include shipping cost into the product price, or will set a flat rate cost and cover the remaining amount. We’ve partnered with Shippo to give you discounted shipping rates, so be sure to setup an account above.`;
    default:
      break;
  }
};

const useStyles = makeStyles((theme) => ({
  customWidth: {
    maxWidth: 300,
    fontSize: "14px",
  },
}));

function StoreSettings(props) {
  const {
    storeSettings,
    activeApp: { shippoToken },
  } = props;
  // console.dir(props);

  const scrollTo = (ref) => {
    if (ref && props.history.action === "PUSH" /* + other conditions */) {
      ref.scrollIntoView({ behavior: "smooth", block: "start" });
      props.history.action = "POP";
    }
  };
  const [settingsState, setSettingsState] = useState(storeSettings);
  const [showAddressInput, setAddressInput] = useState(false);
  const [addressType, setAddressType] = useState("");
  const [showShippingFreeAfter, setShippingFreeAfter] = useState(
    storeSettings.shippingFreeAfter
  );

  const [showShippoModal, setShowShippoModal] = useState(false);

  const [showDeliveryFreeAfter, setDeliveryFreeAfter] = useState(
    storeSettings.deliveryFreeAfter
  );

  const toggleShippoModal = () => {
    setShowShippoModal(!showShippoModal);
  };

  const shippoButtons = [
    <a
      key="cancelButton"
      style={{
        backgroundColor: "white",
        cursor: "pointer",
        color: "#1976d2",
        lineHeight: "1.75",
        borderRadius: "4px",
        letterSpacing: "0.02857em",
        textTransform: "uppercase",
        fontSize: "0.9375rem",
      }}
      onClick={toggleShippoModal}
    >
      Cancel
    </a>,
    <a
      onClick={() => {
        window.open(
          "https://goshippo.com/oauth/authorize?response_type=code&client_id=d422b1931ad54576ae69dd8a23ee7de5&scope=*&state=" +
            JSON.stringify({
              appId: props.activeApp.id,
              validation: "m360Shippo",
            }),
          "_blank"
        );
      }}
      key="confirmButton"
      style={{
        cursor: "pointer",
        backgroundColor: "white",
        color: "#89d2d3",
        lineHeight: "1.75",
        borderRadius: "4px",
        letterSpacing: "0.02857em",
        textTransform: "uppercase",
        paddingLeft: "10px",
        fontSize: "0.9375rem",
      }}
    >
      Continue to Shippo
    </a>,
  ];

  const renderShippoModal = () => {
    if (!showShippoModal) return null;

    return (
      <AlertModal
        title="Discounted Shipping with Shippo"
        buttons={shippoButtons}
        render={showShippoModal}
      >
        <P>
          We've partnered with Shippo to provide you discounted shipping,
          exclusively available on Mobile360. After setting up a Shippo account,
          your Mobile360 shipping orders will automatically sync with Shippo,
          where you can purchase discounted shipping labels. Please note, all
          things shipping will be handled by Shippo themselves, including
          payment label. By continuing, you agree to the terms set by Mobile360
          and Shippo.
        </P>
      </AlertModal>
    );
  };

  const {
    additionalDeliveryCost,
    additionalShippingCost,
    allowCustomizedPurchases,
    deliveryAvailable,
    deliveryCenterPoint,
    pickupAddress,
    deliveryFreeAfter,
    deliveryRadius,
    pickupAvailable,
    postDeliveryOrderNotes,
    postPickupOrderNotes,
    postShippingOrderNotes,
    shippingAvailable,
    shippingFreeAfter,
  } = settingsState;

  const toggleShowShippingFree = () => {
    setShippingFreeAfter(!showShippingFreeAfter);
  };
  const toggleShowDeliveryFree = () => {
    setDeliveryFreeAfter(!showDeliveryFreeAfter);
  };
  const goBack = () => {
    props.history.push("/application");
  };

  const isTruthyFalsey = (field, value) => {
    if (
      field === "allowCustomizedPurchases" ||
      field === "deliveryAvailable" ||
      field === "pickupAvailable" ||
      field === "shippingAvailable"
    ) {
      if (value === 1) {
        return true;
      } else return false;
    }
    return value;
  };

  const handleChange = (event) => {
    setSettingsState({
      ...settingsState,
      [event.target.name]: event.target.checked,
    });
  };

  const update = (field) => (value) => {
    value = isTruthyFalsey(field, value);
    // console.log("updating " + field + " to " + value);
    if (field === "deliveryRadius") {
      value = parseInt(value["floatValue"]);
    }
    if (
      field === "shippingFreeAfter" ||
      field === "additionalShippingCost" ||
      field === "deliveryFreeAfter" ||
      field === "additionalDeliveryCost"
    ) {
      value = formatDollarsToCents(value["value"]);
    }
    setSettingsState({
      ...settingsState,
      [field]: value,
    });
  };

  const save = () => {
    // console.dir(props);
    props.actions.updateStoreSettings(settingsState, props.activeApp.id);
  };

  const updateAddress = (field, value) => {
    // console.log("updating " + field + " to " + value);
    setSettingsState({
      ...settingsState,
      [field]: value,
    });
  };

  const toggleAddressInput = (type) => {
    setAddressType(type);
    setAddressInput(!showAddressInput);
  };

  const myRef = useRef(null);
  const renderAddress = (address) => {
    if (address) {
      return (
        <span
          className={titleStyle}
          style={{ color: "#89d2d3", fontWeight: "bold", marginRight: "20px" }}
        >
          {/* 9876 Dublin St, San Jose, CA 95132 */}
          {address.streetAddress ? address.streetAddress + ", " : null}
          {address.city ? address.city + ", " : null}
          {address.state ? address.state + " " : null}
          {address.zip ? address.zip : null}
        </span>
      );
    } else {
      return (
        <span
          className={titleStyle}
          style={{ color: "#89d2d3", fontWeight: "bold" }}
        >
          {address.fullAddress ? address.fullAddress : null}
        </span>
      );
    }
  };

  const renderAddressInputModal = () => {
    if (!showAddressInput) return null;
    let addressToEdit = null;
    if (addressType === "Pickup") {
      addressToEdit = pickupAddress;
    } else if (addressType === "Delivery Center Point") {
      addressToEdit = deliveryCenterPoint;
    }
    return (
      <AddressModal
        address={addressToEdit}
        addressType={addressType}
        onClose={toggleAddressInput}
        open={showAddressInput}
        updateAddress={updateAddress}
      />
    );
  };
  const classes = useStyles();
  return (
    <ThemeProvider theme={theme}>
      {renderAddressInputModal()}
      {renderShippoModal()}
      <a ref={scrollTo}></a>

      <div className={storeContainer}>
        <FontAwesomeIcon
          className={arrowStyle}
          icon="arrow-left"
          onClick={goBack}
        />
      </div>
      <div className={storeContainer}>
        <H1 className={headerText}>Store Settings</H1>
        <FlexRight>
          <Button
            style={{
              width: "100px",
              display: "inline-block",
              margin: "0px",
              textAlign: "center",
              marginLeft: "20px",
              height: "42px",
            }}
            className={mdcButton}
            onClick={save}
          >
            Save
          </Button>
        </FlexRight>
        {/* Pick Up */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontSize: "15px",
              margin: "13px 15px 15px 0px",
            }}
          >
            Pickup Available
          </p>

          <Switch
            checked={pickupAvailable}
            onChange={handleChange}
            color="primary"
            name="pickupAvailable"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>

        {pickupAvailable ? (
          <div>
            <p
              style={{
                fontSize: "15px",
                margin: "15px 0px",
                marginRight: "15px",
              }}
            >
              Pickup Location:
            </p>
            <div style={{ marginBottom: "20px" }}>
              {pickupAddress ? (
                <div>
                  {renderAddress(pickupAddress)}
                  <Button
                    style={{
                      width: "70px",
                      display: "inline-block",
                      margin: "0px",
                      textAlign: "center",
                      height: "42px",
                    }}
                    className={mdcButton}
                    onClick={() => toggleAddressInput("Pickup")}
                  >
                    Edit
                  </Button>
                </div>
              ) : (
                <Button
                  style={{
                    width: "130px",
                    display: "inline-block",
                    margin: "0px",
                    textAlign: "center",
                    height: "42px",
                  }}
                  className={mdcButton}
                  onClick={() => toggleAddressInput("Pickup")}
                >
                  Add Address
                </Button>
              )}
            </div>
            <div style={{ display: "flex" }}>
              <span className={titleStyle} style={{ marginRight: "5px" }}>
                Pickup message to customer:
              </span>
              <Tooltip
                title={getShippingTipText("PICKUP")}
                classes={{ tooltip: classes.customWidth }}
              >
                <HelpTwoToneIcon color="primary" fontSize="small" />
              </Tooltip>
            </div>
            <TextArea
              // width="100%"
              height="5rem"
              placeholder="i.e. Thanks for your order! You’ll receive a notification when it’s ready for pickup. Please pickup before store closes today."
              text={postPickupOrderNotes}
              onChange={update("postPickupOrderNotes")}
              // disabled={!isNewStoreItem}
            />
          </div>
        ) : null}

        {/*  Shipping */}
        <hr
          style={{
            borderTop: "2px dotted #89d2d3",
            margin: "20px 0px",
            width: "100%",
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontSize: "15px",
              margin: "13px 15px 15px 0px",
            }}
          >
            Shipping Available
          </p>
          <Switch
            checked={shippingAvailable}
            onChange={handleChange}
            color="primary"
            name="shippingAvailable"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>
        {shippoToken && shippingAvailable && (
          <div
            style={{
              cursor: "pointer",
              fontSize: "15px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <Button
              style={{
                display: "inline-flex",
                alignItems: "center",
                margin: "0px",
                textAlign: "center",
                height: "42px",
                boxShadow: "3px 3px 13px rgba(120, 120, 120, 0.5)",
              }}
              onClick={
                () =>
                  // getStripeUrl(id).then((url) => {
                  Object.assign(document.createElement("a"), {
                    target: "_blank",
                    href: "https://goshippo.com/",
                  }).click()
                // })
              }
            >
              {/* <FontAwesomeIcon icon="truck" /> */}
              <a
                style={{
                  fontWeight: "bold",
                  color: "white",
                  textDecoration: "none",
                  marginLeft: "5px",
                }}
              >
                Go to Shippo
              </a>
            </Button>
          </div>
        )}

        {shippingAvailable ? (
          <div>
            {!shippoToken ? (
              <div
                style={{
                  margin: "10px 0px",
                }}
              >
                <Alert severity="info">
                  Mobile360 has integrated with Shippo for secure shipping and
                  handling. With Shippo, you get the absolute best rates from
                  top carriers and everything you need for professional shipping
                  in one place. Once you are set up with Shippo, you will be
                  able to
                  <ul>
                    <li>Manage all your orders in one place.</li>
                    <li>
                      Create discounted shipping labels from USPS, UPS, Fedex,
                      DHL and more.
                    </li>
                    <li>Track your shipments and notify your customers.</li>
                    <li>Streamline returns with free return labels.</li>
                  </ul>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "10px",
                    }}
                  >
                    <Button
                      style={{
                        // width: "70px",
                        display: "inline-block",
                        margin: "0px",
                        textAlign: "center",
                        // height: "42px",
                      }}
                      className={mdcButton}
                      onClick={toggleShippoModal}
                    >
                      Setup Discounted Shipping via Shippo
                    </Button>
                  </div>
                </Alert>
              </div>
            ) : null}
            <div style={{ display: "flex", marginBottom: "10px" }}>
              <span className={titleStyle} style={{ marginRight: "5px" }}>
                Flat Rate Shipping Cost:
              </span>
              <Tooltip
                title={getShippingTipText("SHIPPO")}
                classes={{ tooltip: classes.customWidth }}
              >
                <HelpTwoToneIcon color="primary" fontSize="small" />
              </Tooltip>
            </div>
            <NumberFormat
              placeholder="Shipping Cost"
              thousandSeparator={true}
              prefix={"$"}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              allowLeadingZeros={false}
              defaultValue={
                additionalShippingCost
                  ? priceToDollars(additionalShippingCost)
                  : null
              }
              onValueChange={update("additionalShippingCost")}
              className={inputStyle}
              // disabled={!isNewStoreItem}
            />
            <div style={{ display: "flex" }}>
              <span className={titleStyle} style={{ marginRight: "5px" }}>
                Shipping message to customer:
              </span>
              <Tooltip
                title={getShippingTipText("SHIPPING")}
                classes={{ tooltip: classes.customWidth }}
              >
                <HelpTwoToneIcon color="primary" fontSize="small" />
              </Tooltip>
            </div>
            <TextArea
              // width="100%"
              height="5rem"
              placeholder="i.e. Thanks for your order! Item will be delivered in 7-10 business days. We will email you with tracking information."
              text={postShippingOrderNotes}
              onChange={update("postShippingOrderNotes")}
              style={{ marginTop: "10px" }}
              // disabled={!isNewStoreItem}
            />
          </div>
        ) : null}

        {/* Delivery */}
        <hr
          style={{
            borderTop: "2px dotted #89d2d3",
            margin: "20px 0px",
            width: "100%",
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <p
            style={{
              fontSize: "15px",
              margin: "13px 15px 15px 0px",
            }}
          >
            Delivery Available
          </p>
          <Switch
            checked={deliveryAvailable}
            onChange={handleChange}
            color="primary"
            name="deliveryAvailable"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>

        {deliveryAvailable ? (
          <div>
            <NumberFormat
              placeholder="Delivery Charge"
              thousandSeparator={true}
              prefix={"$"}
              decimalScale={2}
              fixedDecimalScale={true}
              allowNegative={false}
              allowLeadingZeros={false}
              defaultValue={
                additionalDeliveryCost
                  ? priceToDollars(additionalDeliveryCost)
                  : null
              }
              onValueChange={update("additionalDeliveryCost")}
              className={inputStyle}
              // disabled={!isNewStoreItem}
            />
            <div>
              <p
                style={{
                  fontSize: "15px",
                  margin: "15px 0px",
                  marginRight: "15px",
                  display: "inline-block",
                }}
              >
                Setup Delivery Radius:
              </p>
              <NumberFormat
                placeholder="Delivery Radius"
                thousandSeparator={true}
                suffix={" miles"}
                decimalScale={0}
                fixedDecimalScale={true}
                allowNegative={false}
                allowLeadingZeros={false}
                defaultValue={deliveryRadius ? deliveryRadius : null}
                onValueChange={update("deliveryRadius")}
                className={inputStyle}
                style={{ display: "inline-block", width: "inherit" }}
                // disabled={!isNewStoreItem}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              {deliveryCenterPoint ? (
                <div>
                  <p
                    style={{
                      fontSize: "15px",
                      margin: "15px 0px",
                      marginRight: "15px",
                      display: "inline-block",
                    }}
                  >
                    From
                  </p>
                  {renderAddress(deliveryCenterPoint)}
                  <Button
                    style={{
                      width: "70px",
                      display: "inline-block",
                      margin: "0px",
                      textAlign: "center",
                      height: "42px",
                    }}
                    className={mdcButton}
                    onClick={() => toggleAddressInput("Delivery Center Point")}
                  >
                    Edit
                  </Button>
                </div>
              ) : (
                <Button
                  style={{
                    width: "130px",
                    display: "inline-block",
                    margin: "0px",
                    textAlign: "center",
                    height: "42px",
                  }}
                  className={mdcButton}
                  onClick={() => toggleAddressInput("Delivery Center Point")}
                >
                  Add Address
                </Button>
              )}
            </div>
            <div style={{ display: "flex" }}>
              <span className={titleStyle} style={{ marginRight: "5px" }}>
                Delivery message to customer:
              </span>
              <Tooltip
                title={getShippingTipText("DELIVERY")}
                classes={{ tooltip: classes.customWidth }}
              >
                <HelpTwoToneIcon color="primary" fontSize="small" />
              </Tooltip>
            </div>
            <TextArea
              // width="100%"
              height="5rem"
              placeholder="i.e. Items will be delivered ASAP. We will call you before delivering to confirm."
              text={postDeliveryOrderNotes}
              onChange={update("postDeliveryOrderNotes")}
              style={{ marginBottom: "10px" }}
              // disabled={!isNewStoreItem}
            />
          </div>
        ) : null}
      </div>
    </ThemeProvider>
  );
}

export default withRedux(StoreSettings);
